<template>
  <div class="content-wrapper ataviti-home">

    <!-- LOADING -->
    <div
      v-if="loading"
      class="content-body"
    >
      <div class="chantier-state">
        <b-spinner class="mb-1" />
        <p class="mt-1 mb-0">
          Chargement…
        </p>
      </div>
    </div>

    <!-- LOAD ERROR -->
    <div
      v-else-if="loadError"
      class="content-body"
    >
      <div class="chantier-state chantier-state--error">
        <feather-icon
          icon="AlertTriangleIcon"
          size="32"
        />
        <p class="mt-1 mb-1">
          {{ loadError }}
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="load"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-50"
          />
          Réessayer
        </b-button>
      </div>
    </div>

    <div
      v-else-if="plan && chantier"
      class="content-body"
    >

      <router-link
        :to="{ name: 'chantier-plan-detail', params: { chantierId: chantier.id, planId: plan.id } }"
        class="chantier-back"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="14"
          class="mr-50"
        />
        Retour au plan d'action
      </router-link>

      <!-- HERO -->
      <header class="ataviti-home__banner">
        <div class="ataviti-home__banner-text">
          <p class="ataviti-home__banner-eyebrow">
            {{ chantier.nom }} — {{ plan.libelle }}
          </p>
          <h1 class="ataviti-home__title">
            {{ isEdit ? 'Modifier la note de réunion' : 'Nouvelle note de réunion' }}
          </h1>
        </div>
      </header>

      <b-form @submit.prevent="onSubmit">

        <!-- HEADER FIELDS -->
        <section class="chantier-section">
          <h2 class="chantier-section__title">
            <feather-icon
              icon="EditIcon"
              size="18"
              class="mr-50"
            />
            Informations
          </h2>
          <div class="row">
            <div class="col-12 col-md-8">
              <b-form-group
                label="Titre de la note"
                label-for="note-titre"
              >
                <b-form-input
                  id="note-titre"
                  v-model="form.titre"
                  placeholder="Ex. Atelier — cadrage de la non-conformité NC1"
                  required
                />
              </b-form-group>
            </div>
            <div class="col-12 col-md-4">
              <b-form-group
                label="Date"
                label-for="note-date"
              >
                <b-form-datepicker
                  id="note-date"
                  v-model="form.date"
                  locale="fr"
                  :date-format-options="{ year: 'numeric', month: 'long', day: 'numeric' }"
                  placeholder="Choisir une date"
                />
              </b-form-group>
            </div>
          </div>

          <b-form-group
            v-if="reunions.length"
            label="Réunion planifiée associée (optionnel)"
            label-for="note-reunion"
          >
            <b-form-select
              id="note-reunion"
              v-model="form.reunionPlanifieeId"
              :options="reunionOptions"
            />
          </b-form-group>
        </section>

        <!-- NOTES INTERNES -->
        <section class="chantier-section">
          <h2 class="chantier-section__title">
            <feather-icon
              icon="LockIcon"
              size="18"
              class="mr-50"
            />
            Notes internes
          </h2>

          <div class="note-form__internal-banner">
            <feather-icon
              icon="EyeOffIcon"
              size="13"
              class="mr-50"
            />
            Interne — visible uniquement par les participants au chantier.
            Ne sera jamais affichée à un utilisateur externe, même si le chantier est public.
          </div>

          <ressifnet-quill-editor
            ref="notesEditor"
            v-model="form.notesInternesHtml"
            class="note-form__editor"
          />
          <dictation-button
            v-if="hasAudioTranscription"
            :transcribe="onTranscribeAudio"
            @final-text="onDictatedText"
          />
          <b-button
            v-if="hasAudioTranscription"
            size="sm"
            variant="outline-primary"
            class="mt-50"
            :disabled="formatting"
            @click="onFormatNote"
          >
            <b-spinner
              v-if="formatting"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="ZapIcon"
              size="14"
              class="mr-50"
            />
            Mettre en forme
          </b-button>
          <b-button
            v-if="formattedSnapshot"
            size="sm"
            variant="outline-secondary"
            class="mt-50 ml-50"
            :disabled="formatting"
            @click="onToggleFormat"
          >
            <feather-icon
              :icon="formatUndone ? 'RotateCwIcon' : 'RotateCcwIcon'"
              size="14"
              class="mr-50"
            />
            {{ formatUndone ? 'Rétablir la mise en forme' : 'Annuler la mise en forme' }}
          </b-button>
          <span
            v-if="formatError"
            class="text-danger ml-50"
          >{{ formatError }}</span>
        </section>

        <!-- COMPTE-RENDU PUBLIC : indisponible sur un chantier privé -->
        <section
          v-if="chantier.visibilite === 1"
          class="chantier-section"
        >
          <div class="chantier-section__head">
            <h2 class="chantier-section__title">
              <feather-icon
                icon="GlobeIcon"
                size="18"
                class="mr-50"
              />
              Compte-rendu public
              <span class="chantier-section__optional">— optionnel</span>
            </h2>
            <!--
              DEFERRED FEATURE (kept in code for a later spec, hidden for the MVP):
              IA-generated summary of the public compte-rendu.
              See plan 2026-05-29-chantiers-front.md (features reportées).
            -->
            <b-button
              v-if="false"
              variant="outline-primary"
              size="sm"
              @click="onGenerateAI"
            >
              <feather-icon
                icon="ZapIcon"
                size="14"
                class="mr-50"
              />
              Générer via IA
            </b-button>
          </div>

          <div class="note-form__public-banner">
            <feather-icon
              icon="GlobeIcon"
              size="13"
              class="mr-50"
            />
            Visible par tous les utilisateurs du CRM, y compris les utilisateurs externes.
          </div>

          <b-form-group
            label-for="note-cr-public"
          >
            <b-form-textarea
              id="note-cr-public"
              v-model="form.compteRenduPublic"
              rows="5"
              placeholder="Rédigez un résumé public de la réunion, ou laissez vide si non pertinent."
            />
          </b-form-group>
        </section>

        <!-- Chantier privé : pas de compte-rendu public -->
        <section
          v-else
          class="chantier-section"
        >
          <h2 class="chantier-section__title">
            <feather-icon
              icon="LockIcon"
              size="18"
              class="mr-50"
            />
            Compte-rendu public
          </h2>
          <p class="chantier-section__text chantier-empty-inline">
            Ce chantier est privé : le compte-rendu public n'est pas disponible.
          </p>
        </section>

        <!-- ACTIONS -->
        <p
          v-if="saveError"
          class="text-danger note-form__error"
        >
          {{ saveError }}
        </p>
        <div class="note-form__actions">
          <b-button
            variant="outline-secondary"
            :disabled="saving"
            @click="onCancel"
          >
            Annuler
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="saving"
          >
            <b-spinner
              v-if="saving"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              size="14"
              class="mr-50"
            />
            Enregistrer
          </b-button>
        </div>

      </b-form>
    </div>

    <div
      v-else
      class="content-body"
    >
      <div class="chantier-warning">
        <feather-icon
          icon="AlertTriangleIcon"
          size="16"
          class="mr-50"
        />
        Contexte introuvable — impossible d'ouvrir le formulaire.
      </div>
    </div>
  </div>
</template>

<script>
import {
  BForm,
  BButton,
  BSpinner,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormDatepicker,
  BFormSelect,
} from 'bootstrap-vue'
import RessifnetQuillEditor from '@/components/inputs/RessifnetQuillEditor.vue'
import DictationButton from '@/components/inputs/DictationButton.vue'
import appSettings from '@/appSettings'
import { toDateInput, todayDateInput } from './date-input'

export default {
  name: 'NoteFormView',
  components: {
    BForm,
    BButton,
    BSpinner,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormDatepicker,
    BFormSelect,
    RessifnetQuillEditor,
    DictationButton,
  },
  data() {
    return {
      chantier: null,
      plan: null,
      loading: true,
      loadError: null,
      saving: false,
      saveError: null,
      formatting: false,
      formatError: null,
      originalSnapshot: null, // HTML de la note AVANT la dernière mise en forme
      formattedSnapshot: null, // HTML produit par la dernière mise en forme
      formatUndone: false, // true = on affiche l'original, false = la version mise en forme
      form: {
        titre: '',
        date: todayDateInput(),
        reunionPlanifieeId: null,
        notesInternesHtml: '',
        compteRenduPublic: '',
      },
    }
  },
  computed: {
    hasAudioTranscription() {
      return appSettings.hasAudioTranscription()
    },
    // The current "nouvelle note" route is create-only; edit is supported only
    // if a route ever provides a noteId param.
    noteId() {
      return this.$route.params.noteId || null
    },
    isEdit() {
      return !!this.noteId
    },
    canEdit() {
      // Édition de note = droit Update + visibilité interne, et JAMAIS sur un chantier archivé
      // (aligné sur ChantierDetailView/PlanActionDetailView et la garde serveur des commandes de note).
      return !!(this.chantier && this.chantier.userCanManage
        && this.chantier.userCanSeeInternal && this.$can('CHANTIER:update')
        && !this.chantier.archivedAt)
    },
    reunions() {
      return (this.plan && this.plan.reunionsPlanifiees) || []
    },
    reunionOptions() {
      return [
        { value: null, text: 'Aucune réunion associée' },
        ...this.reunions.map(r => ({ value: r.id, text: r.libelle })),
      ]
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.loadError = null
      const { chantierId, planId } = this.$route.params
      try {
        const chantier = await this.$http.myressif.chantiers.getById(chantierId)
        this.chantier = chantier
        this.plan = (chantier.plansAction || []).find(p => p.id === planId) || null
        // Pas d'édition possible (chantier archivé ou droits insuffisants) : retour au plan en lecture seule
        // plutôt que de présenter un formulaire que l'API rejettera (Forbidden).
        if (!this.canEdit) {
          this.$router.replace({ name: 'chantier-plan-detail', params: { chantierId, planId } })
          return
        }
        if (this.isEdit && this.plan) {
          const note = await this.$http.myressif.chantiers.getNote(chantierId, planId, this.noteId)
          this.form = {
            titre: note.titre || '',
            date: note.date ? toDateInput(note.date) : todayDateInput(),
            reunionPlanifieeId: note.reunionPlanifieeId || null,
            notesInternesHtml: note.notesInternesHtml || '',
            compteRenduPublic: note.compteRenduPublic || '',
          }
        }
      } catch (e) {
        this.chantier = null
        this.plan = null
        this.loadError = 'Une erreur est survenue lors du chargement.'
      } finally {
        this.loading = false
      }
    },
    async onTranscribeAudio(audio) {
      return this.$http.myressif.chantiers.transcribeAudio(audio.blob, audio.fileName)
    },
    onDictatedText(text) {
      const editor = this.$refs.notesEditor
      if (editor && editor.insertTextAtCursor) editor.insertTextAtCursor(text)
    },
    async onFormatNote() {
      const editor = this.$refs.notesEditor
      if (!editor) return
      const text = editor.getPlainText ? editor.getPlainText().trim() : ''
      if (!text) { this.formatError = 'La note est vide.'; return }
      const snapshot = this.form.notesInternesHtml // contenu courant AVANT mise en forme
      this.formatting = true
      this.formatError = null
      try {
        const html = await this.$http.myressif.chantiers.formatNote(text)
        if (html) {
          editor.setHtml(html)
          this.originalSnapshot = snapshot
          this.formattedSnapshot = html
          this.formatUndone = false
        }
      } catch (e) {
        this.formatError = 'La mise en forme a échoué. Réessayez.'
      } finally {
        this.formatting = false
      }
    },
    // Bascule entre le texte d'origine et la version mise en forme (persiste jusqu'au prochain formatage).
    onToggleFormat() {
      const editor = this.$refs.notesEditor
      if (!editor || !editor.setHtml) return
      if (this.formatUndone) {
        editor.setHtml(this.formattedSnapshot) // rétablir la mise en forme
        this.formatUndone = false
      } else {
        editor.setHtml(this.originalSnapshot) // revenir au texte d'origine
        this.formatUndone = true
      }
      this.formatError = null
    },
    onCancel() {
      this.$router.push({
        name: 'chantier-plan-detail',
        params: { chantierId: this.chantier.id, planId: this.plan.id },
      })
    },
    async onSubmit() {
      this.saving = true
      this.saveError = null
      const { chantierId, planId } = this.$route.params
      const model = {
        reunionPlanifieeId: this.form.reunionPlanifieeId || undefined,
        date: this.form.date,
        titre: this.form.titre,
        notesInternesHtml: this.form.notesInternesHtml || undefined,
        compteRenduPublic: this.form.compteRenduPublic || undefined,
      }
      try {
        if (this.isEdit) {
          await this.$http.myressif.chantiers.updateNote(chantierId, planId, this.noteId, model)
        } else {
          await this.$http.myressif.chantiers.createNote(chantierId, planId, model)
        }
        this.$router.push({
          name: 'chantier-plan-detail',
          params: { chantierId, planId },
        })
      } catch (e) {
        this.saveError = "L'enregistrement de la note a échoué."
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
$at-green:   #1c4d4d;
$at-yellow:  #fcf5a6;
$at-orange:  #ff6131;
$at-beige:   #fffced;
$at-ink-900: #0F1F1F;
$at-ink-700: #2A3F3F;
$at-ink-500: #5A7373;
$at-ink-100: #E4ECEC;

.chantier-back {
  display: inline-flex;
  align-items: center;
  color: $at-ink-500;
  font-size: 13px;
  margin-bottom: 12px;
  text-decoration: none;
  &:hover { color: $at-orange; }
}

.chantier-state {
  text-align: center;
  padding: 48px 16px;
  color: $at-ink-500;
  background: #fff;
  border: 1px dashed $at-ink-100;
  border-radius: 10px;

  &--error {
    color: $at-orange;
    border-color: rgba(255, 97, 49, .3);
  }
}

.note-form__error {
  margin: 0 0 12px;
  text-align: right;
}

.ataviti-home {
  &__banner {
    background: $at-yellow;
    border-radius: 12px;
    padding: 28px 32px;
    margin-bottom: 24px;
  }
  &__banner-eyebrow {
    color: $at-green;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: .08em;
    text-transform: uppercase;
    margin: 0 0 8px;
  }
  &__title {
    font-family: 'Iowan Old Style', 'Palatino Linotype', Georgia, serif;
    font-size: 28px;
    color: $at-green;
    font-weight: 500;
    margin: 0;
    letter-spacing: -.01em;
  }
}

.chantier-section {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 18px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
    .chantier-section__title { margin-bottom: 0; }
  }
  &__title {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: $at-ink-900;
    font-weight: 600;
    margin: 0 0 14px;
  }
  &__optional {
    color: $at-ink-500;
    font-size: 13px;
    font-weight: 400;
    margin-left: 8px;
    font-style: italic;
  }
}

.chantier-warning {
  display: flex;
  align-items: center;
  background: rgba(240, 168, 48, .12);
  border: 1px solid rgba(240, 168, 48, .35);
  color: #8a5a0a;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 18px;
  font-size: 13px;
}

.note-form {
  &__editor {
    margin-top: 8px;
    background: #fff;
    border: 1px solid $at-ink-100;
    border-radius: 6px;

    ::v-deep .ql-toolbar {
      background: $at-beige;
      border-bottom: 1px solid $at-ink-100;
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
    }
    ::v-deep .ql-container {
      border: none;
      font-family: inherit;
    }
    ::v-deep .ql-editor {
      min-height: 240px;
      font-size: 14px;
      line-height: 1.65;
      color: $at-ink-900;
      background: #fff;
    }
  }
  &__internal-banner {
    background: rgba(255, 97, 49, .08);
    color: $at-orange;
    border-left: 3px solid $at-orange;
    font-size: 12px;
    font-weight: 500;
    padding: 8px 12px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    margin-bottom: 14px;
  }
  &__public-banner {
    background: rgba(47, 125, 107, .08);
    color: #2f7d6b;
    border-left: 3px solid #2f7d6b;
    font-size: 12px;
    font-weight: 500;
    padding: 8px 12px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    margin-bottom: 14px;
  }
  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 12px;
  }
}

/* Force-visible form controls — defensive against theme overrides */
::v-deep .form-control,
::v-deep .b-form-datepicker,
::v-deep .custom-select {
  background-color: #fff !important;
  border: 1px solid $at-ink-100 !important;
  color: $at-ink-900 !important;
  min-height: 38px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}
::v-deep .form-control:focus,
::v-deep .b-form-datepicker:focus-within,
::v-deep .custom-select:focus {
  border-color: $at-green !important;
  box-shadow: 0 0 0 3px rgba(28, 77, 77, .12) !important;
  outline: none;
}
::v-deep textarea.form-control {
  min-height: 110px;
}
::v-deep .form-group > label,
::v-deep legend.col-form-label {
  font-size: 12px;
  font-weight: 600;
  color: $at-ink-500;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 4px;
}

/* Hovers on form-level buttons */
::v-deep .btn {
  transition: transform 120ms ease, box-shadow 160ms ease, background-color 160ms ease, color 160ms ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(28, 77, 77, .12);
  }
}
</style>
